import { useCallback, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useMachine } from '@xstate/react';
import { isAxiosError } from 'axios';
import {
  jobsFSM,
  EventType,
  JobPayload,
  State,
  Status,
  CandidateOfferEventType,
} from '../../fsm/jobs';
import type { RecordOfferEventPayload } from './components/candidate/record-offer-event-modal';
import { useAccount } from '../../context/account.context';
import { useCandidateInterview } from './hooks/useCandidateInterview';
import { useCandidateOfferEvents } from './hooks/useCandidateOfferEvents';
import { useCandidateResume } from './hooks/useCandidateResume';

import { message } from 'antd';

import JobList from './pages/job-list';
import CreateJob from './pages/create-job';
import JobDetails from './pages/job-details';
import Candidates from './pages/candidates';
import Candidate from './pages/candidate';
import JobInterviewer from './pages/job-interviewer';
import { isInterviewerSetupEnabled } from './interviewer/flag';
import { useInterviewerService } from './interviewer/serviceContext';
import type { InterviewerConfig } from './services/interviewer.types';

const JobsRoutes = () => {
  const [state, send] = useMachine(jobsFSM);
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const { user } = useAccount();
  // Not part of jobsFSM: the machine drops an event sent while it is busy, and
  // this one is always sent while the candidate request is still in flight.
  const candidateInterview = useCandidateInterview();
  // Same reason: FETCH_OFFER_EVENTS was dropped for the same reason
  // GET_CANDIDATE_INTERVIEW was - sent in the same commit, while the machine
  // was already busy with GET_CANDIDATE. Recording is owned here too, not
  // split across two sources of truth - see the hook's own doc comment.
  const candidateOfferEvents = useCandidateOfferEvents();
  // Same again for the resume link, and it must open its tab during the click:
  // see the hook's doc comment. A failure is said here, not swallowed.
  const showResumeError = useCallback(
    (content: string) => {
      messageApi.open({ type: 'error', content });
    },
    [messageApi],
  );
  const candidateResume = useCandidateResume(showResumeError);

  const handleFetchJobs = useCallback(
    (
      pageNumber: number,
      pageSize: number,
      sort?: string,
      order?: string,
      status?: string,
      includeSynthetic?: boolean,
    ) => {
      send({
        type: EventType.FETCH_JOB_LIST,
        payload: {
          pageNumber,
          pageSize,
          sort,
          order,
          status,
          includeSynthetic,
        },
      });
    },
    [send],
  );

  // The interviewer the recruiter shaped while posting, saved once the job
  // exists (it needs the new job's id). Only ever set with the setup on.
  const interviewerService = useInterviewerService();
  const pendingInterviewer = useRef<InterviewerConfig | null>(null);

  const handleCreateJob = useCallback(
    (job: JobPayload, interviewer?: InterviewerConfig) => {
      pendingInterviewer.current = interviewer ?? null;
      send({ type: EventType.CREATE_JOB, payload: { job } });
    },
    [send],
  );

  const handleFetchAutoPopulatedValues = useCallback(
    (title: string, description: string) => {
      send({
        type: EventType.FETCH_JOB_AUTO_POPULATED_VALUES,
        payload: { title, description },
      });
    },
    [send],
  );

  const handleResetAutoPopulatedValues = useCallback(() => {
    send({
      type: EventType.RESET_JOB_AUTO_POPULATED_VALUES,
    });
  }, [send]);

  const handleFetchDetails = useCallback(
    (id: number) => {
      send({ type: EventType.FETCH_JOB_BY_ID, payload: { id } });
    },
    [send],
  );

  const handleUpdateDetails = useCallback(
    (id: number, job: JobPayload) => {
      send({ type: EventType.UPDATE_JOB, payload: { id, job } });
    },
    [send],
  );

  const handleFetchCandidates = useCallback(
    (
      jobId: number,
      pageNumber: number,
      pageSize: number,
      fitScore?: number,
      statuses?: Status[],
      hasMandatorySkills?: boolean,
      hasMandatoryTitleMatch?: boolean,
      maxDistance?: number,
      candidatesWithNoLocation?: boolean,
      isApplicant?: boolean,
      sort?: string,
      order?: string,
    ) => {
      send({
        type: EventType.FETCH_CANDIDATES,
        payload: {
          jobId,
          pageNumber,
          pageSize,
          fitScore,
          statuses,
          hasMandatorySkills,
          hasMandatoryTitleMatch,
          maxDistance,
          candidatesWithNoLocation,
          isApplicant,
          sort,
          order,
        },
      });
    },
    [send],
  );

  const handleFetchJobHiringManagers = useCallback(
    (jobId: number) => {
      send({
        type: EventType.FETCH_JOB_HIRING_MANAGERS,
        payload: { jobId },
      });
    },
    [send],
  );

  const handleFetchHiringManagers = useCallback(() => {
    send({
      type: EventType.FETCH_HIRING_MANAGERS,
    });
  }, [send]);

  const handleFetchRecruiters = useCallback(() => {
    send({
      type: EventType.FETCH_RECRUITERS,
    });
  }, [send]);

  const handleAssignHiringManagers = useCallback(
    (jobId: number, hiringManagerId: number) => {
      send({
        type: EventType.ASSIGN_HIRING_MANAGER,
        payload: { jobId, hiringManagerId },
      });
    },
    [send],
  );

  const handleAssignRecruiter = useCallback(
    (jobId: number, recruiterId: number) => {
      send({
        type: EventType.ASSIGN_RECRUITER,
        payload: { jobId, recruiterId },
      });
    },
    [send],
  );

  const handleGetCandidate = useCallback(
    (jobId: number, candidateId: number) => {
      send({
        type: EventType.GET_CANDIDATE,
        payload: { jobId, candidateId },
      });
    },
    [send],
  );

  const handleSendCandidates = useCallback(
    (
      jobId: number,
      candidateIds: React.Key[],
      emails: string[],
      emailBody: string,
    ) => {
      send({
        type: EventType.SEND_CANDIDATES,
        payload: { jobId, candidateIds, emails, emailBody },
      });
    },
    [send],
  );

  const handleAddFeedback = useCallback(
    (
      jobId: number,
      candidateId: number,
      text: string,
      rating?: number,
      reason?: string,
    ) => {
      send({
        type: EventType.ADD_CANDIDATE_FEEDBACK,
        payload: { jobId, candidateId, text, rating, reason },
      });
    },
    [send],
  );

  const handleGetFeedback = useCallback(
    (jobId: number, candidateId: number) => {
      send({
        type: EventType.GET_CANDIDATE_FEEDBACK,
        payload: { jobId, candidateId },
      });
    },
    [send],
  );

  const handleUpdateCandidateStatus = useCallback(
    (jobId: number, candidateId: number, status: string) => {
      send({
        type: EventType.UPDATE_CANDIDATE_STATUS,
        payload: { status, jobId, candidateId },
      });
    },
    [send],
  );

  const handleRecordOfferEvent = useCallback(
    (
      jobId: number,
      candidateId: number,
      eventType: CandidateOfferEventType,
      payload: RecordOfferEventPayload,
    ) => {
      candidateOfferEvents
        .record(jobId, candidateId, eventType, payload)
        .catch((error: unknown) => {
          // Recording is the billing trigger - a failure (a duplicate
          // OFFER_MADE's 409 from avrio-backend#185, a permission error, a
          // network failure) must surface to the recorder, not disappear.
          // This used to send the whole jobs app to its global, exitless
          // ERROR state; now it's a local, retriable toast.
          const message =
            isAxiosError(error) && error.response?.data?.message
              ? error.response.data.message
              : 'Could not record this offer. Please try again.';
          messageApi.open({ type: 'error', content: message });
        });
    },
    [candidateOfferEvents, messageApi],
  );

  const handleShareCandidate = useCallback(
    (jobId: number, candidateId: number, userId: number) => {
      send({
        type: EventType.SHARE_CANDIDATE,
        payload: { jobId, candidateId, userId },
      });
    },
    [send],
  );

  // A failed request used to leave the app in ERROR with nothing on screen
  // saying so. Say what failed. The server's own message is shown only for a
  // client error (4xx), where the backend writes a deliberate sentence (e.g.
  // the invitation fit-score gate), and only if it is short plain text. A 5xx,
  // a network failure or anything longer gets the generic line, so a stack
  // trace or framework error body never reaches the screen.
  useEffect(() => {
    if (state.matches(State.ERROR) && state.context.error) {
      const { status, data } = state.context.error.response ?? {};
      const message = (data as { message?: unknown } | undefined)?.message;
      const isDeliberate =
        typeof status === 'number' &&
        status >= 400 &&
        status < 500 &&
        typeof message === 'string' &&
        message.length > 0 &&
        message.length <= 200;
      messageApi.open({
        type: 'error',
        content: isDeliberate
          ? message
          : 'Something went wrong. Please try again.',
      });
    }
  }, [state.context.error]);

  useEffect(() => {
    if (state.value.match(State.EMAIL_SENT)) {
      messageApi.open({
        type: 'success',
        content: 'Email successfully sent.',
      });
    }
  }, [state.value]);

  useEffect(() => {
    if (state.value.match(State.JOB_ADDED)) {
      messageApi.open({
        type: 'success',
        content: 'Job has been successfully posted.',
      });
      const shaped = pendingInterviewer.current;
      pendingInterviewer.current = null;
      const createdId = state.context.jobList[0]?.id;
      if (shaped && typeof createdId === 'number') {
        // A job just created has no settings yet: its version is 0.
        interviewerService.save(createdId, shaped, 0).catch(() => {
          messageApi.open({
            type: 'warning',
            duration: 8,
            content:
              "The job is posted, but your interviewer changes weren't saved. Open the job's Interviewer tab to set them again.",
          });
        });
      }
      navigate('/jobs');
    }
  }, [state.value]);

  useEffect(() => {
    if (state.value.match(State.JOB_UPDATED)) {
      messageApi.open({
        type: 'success',
        content: 'Job has been successfully updated.',
      });
    }
  }, [state.value]);

  useEffect(() => {
    if (state.value.match(State.SHARE_CANDIDATE)) {
      messageApi.open({
        type: 'success',
        content: 'Candidate profile shared successfully.',
      });
    }
  }, [state.value]);

  return (
    <>
      <Routes>
        <Route
          path="/"
          element={
            <JobList
              user={user}
              data={state.context.jobList}
              totalElements={state.context.totalJobElements}
              isLoading={state.context.isLoading}
              totalPages={state.context.totalJobPages}
              onFetchJobs={handleFetchJobs}
            />
          }
        />
        <Route
          path="/create"
          element={
            <CreateJob
              user={user}
              autoPopulatedValues={state.context.autoPopulatedValues}
              isLoading={state.context.isLoading}
              onCreateJob={handleCreateJob}
              onGetAutoPopulatedValues={handleFetchAutoPopulatedValues}
              onResetAutoPopulatedValues={handleResetAutoPopulatedValues}
            />
          }
        />
        <Route
          path="/:jobId"
          element={
            <JobDetails
              user={user}
              data={state.context.jobDetails}
              hiringManagers={state.context.hiringManagers}
              isLoading={state.context.isLoading}
              onFetchDetails={handleFetchDetails}
              onUpdateDetails={handleUpdateDetails}
              onFetchHiringManagers={handleFetchHiringManagers}
              onFetchRecruiters={handleFetchRecruiters}
              onAssignHiringManagers={handleAssignHiringManagers}
              onAssignRecruiter={handleAssignRecruiter}
            />
          }
        />
        {isInterviewerSetupEnabled() && (
          <Route
            path="/:jobId/interviewer"
            element={
              <JobInterviewer
                user={user}
                data={state.context.jobDetails}
                onFetchDetails={handleFetchDetails}
              />
            }
          />
        )}
        <Route
          path="/:jobId/candidates"
          element={
            <Candidates
              user={user}
              data={state.context.candidates}
              totalElements={state.context.totalCandidateElements}
              isLoading={state.context.isLoading}
              failed={state.matches(State.ERROR)}
              totalPages={state.context.totalCandidatePages}
              jobTitle={state.context.jobDetails?.title}
              onFetchCandidates={handleFetchCandidates}
              onSendCandidates={handleSendCandidates}
              onFetchJobDetails={handleFetchDetails}
            />
          }
        />
        <Route
          path="/:jobId/candidates/:candidateId"
          element={
            <Candidate
              user={user}
              hiringManagers={state.context.hiringManagers}
              isLoading={state.context.isLoading}
              candidate={state.context.candidate}
              candidateLoadFailedFor={state.context.candidateLoadFailedFor}
              candidateLoadRefusal={state.context.candidateLoadRefusal}
              candidateFeedback={state.context.candidateFeedback}
              candidateInterview={candidateInterview.interview}
              candidateInterviewStatus={candidateInterview.status}
              offerEvents={candidateOfferEvents.events}
              isRecordingOfferEvent={candidateOfferEvents.isRecording}
              onGetCandidate={handleGetCandidate}
              onAddFeedback={handleAddFeedback}
              onGetFeedback={handleGetFeedback}
              onGetCandidateInterview={candidateInterview.load}
              onGetOfferEvents={candidateOfferEvents.load}
              onRecordOfferEvent={handleRecordOfferEvent}
              onFetchHiringManagers={handleFetchJobHiringManagers}
              onUpdateCandidateStatus={handleUpdateCandidateStatus}
              onDownloadCandidateCV={candidateResume.open}
              onShareCandidate={handleShareCandidate}
            />
          }
        />
      </Routes>
      {contextHolder}
    </>
  );
};

export default JobsRoutes;
